import { WordRepeat, WordLayoutPart, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

// The placeholder editor's preview (staging feedback, JT1-1887). It writes the changes into the
// document's text the same way the server writes them into the file, so what the admin sees is
// what they get: a repeat's own placeholders first, then the document-wide ones, every match, and
// never inside a placeholder already there.

/** What each list's loop is called in the file, as the server writes it. */
export const LOOP_VARIABLES: Record<string, string> = {
  employment: 'role',
  education: 'study',
  languages: 'language',
  sections: 'section',
  skills: 'skill',
  certifications: 'certification',
};

/** Where `find` starts in `text`, never overlapping a {placeholder}. */
export function occurrences(text: string, find: string): number[] {
  if (!find) return [];
  const locked = Array.from(text.matchAll(/\{[^{}]*\}/g)).map((m) => [m.index ?? 0, (m.index ?? 0) + m[0].length]);
  const found: number[] = [];
  let from = 0;
  for (let at = text.indexOf(find, from); at >= 0; at = text.indexOf(find, from)) {
    const end = at + find.length;
    if (locked.some(([start, stop]) => at < stop && end > start)) {
      from = at + 1;
    } else {
      found.push(at);
      from = end;
    }
  }
  return found;
}

/** The placeholder as it's written into the file: {candidate.full_name}, or {$role.employer} in a repeat. */
export function placeholderText(path: string, repeat?: Pick<WordRepeat, 'list'>): string {
  if (repeat) {
    const variable = LOOP_VARIABLES[repeat.list];
    if (path === repeat.list) return `{$${variable}}`;
    const [list, field] = path.split('.');
    if (list === repeat.list && field) return `{$${variable}.${field}}`;
  }
  return `{${path}}`;
}

/** Where each body paragraph sits: in a table row, or not. */
export function bodyParagraphs(parts: WordLayoutPart[]): Map<number, { table?: number; row?: number }> {
  const where = new Map<number, { table?: number; row?: number }>();
  (parts.find((p) => p.kind === 'body')?.items ?? []).forEach((item) => {
    if (item.type === 'paragraph') where.set(item.index, {});
    else item.rows.forEach((row) => row.cells.flat().forEach((p) => where.set(p.index, { table: item.index, row: row.index })));
  });
  return where;
}

/** The body paragraphs a repeat covers. */
export function coveredBy(repeat: WordRepeat, where: Map<number, { table?: number; row?: number }>): number[] {
  return Array.from(where.entries())
    .filter(([index, at]) => (repeat.table !== undefined ? at.table === repeat.table && at.row === repeat.row : index >= (repeat.from ?? 0) && index <= (repeat.to ?? -1)))
    .map(([index]) => index);
}

/** The repeat a body paragraph is in, if any. */
export function repeatOf(changes: WordPlaceholderChanges, where: Map<number, { table?: number; row?: number }>, part: string, index: number): number {
  if (part !== 'body') return -1;
  return changes.repeats.findIndex((repeat) => coveredBy(repeat, where).includes(index));
}

export interface Preview {
  /** Each paragraph's text with the changes written in, keyed `${part}:${index}`. */
  texts: Map<string, string>;
  /** How many places each change replaces: `g${i}` for document-wide ones, `r${repeat}.${i}` inside a repeat. */
  counts: Map<string, number>;
}

/** Every paragraph's text with the changes written in, and how many places each change hits. */
export function previewChanges(parts: WordLayoutPart[], changes: WordPlaceholderChanges): Preview {
  const where = bodyParagraphs(parts);
  const texts = new Map<string, string>();
  const counts = new Map<string, number>();
  const tally = (key: string, n: number) => counts.set(key, (counts.get(key) ?? 0) + n);
  changes.replacements.forEach((_, i) => counts.set(`g${i}`, 0));
  changes.repeats.forEach((repeat, r) => repeat.replacements.forEach((_, i) => counts.set(`r${r}.${i}`, 0)));

  const write = (part: string, index: number, original: string) => {
    const r = repeatOf(changes, where, part, index);
    const steps = [
      ...(r >= 0 ? changes.repeats[r].replacements.map((c, i) => ({ key: `r${r}.${i}`, find: c.find, insert: placeholderText(c.placeholder, changes.repeats[r]) })) : []),
      ...changes.replacements.map((c, i) => ({ key: `g${i}`, find: c.find, insert: placeholderText(c.placeholder) })),
    ];
    const text = steps.reduce((current, step) => {
      const starts = occurrences(current, step.find);
      tally(step.key, starts.length);
      // From the last match back, so the earlier matches' positions still hold.
      return starts.reduceRight((written, start) => written.slice(0, start) + step.insert + written.slice(start + step.find.length), current);
    }, original);
    texts.set(`${part}:${index}`, text);
  };

  parts.forEach((part) => part.items.forEach((item) => {
    if (item.type === 'paragraph') write(part.part, item.index, item.text);
    else item.rows.forEach((row) => row.cells.flat().forEach((p) => write(part.part, p.index, p.text)));
  }));
  return { texts, counts };
}

/** Text and placeholders, for showing placeholders highlighted. */
export function segments(text: string): { text: string; placeholder: boolean }[] {
  return text.split(/(\{[^{}]*\})/).filter(Boolean).map((piece) => ({ text: piece, placeholder: /^\{[^{}]*\}$/.test(piece) }));
}
